// src/lib/journey-composition-resolver.js — CHANTIER OTA Lot O2
//
// ⚠️ SOURCE UNIQUE pour le serveur ET l'écran (F10 phase 2, 03/10). Écrit en
// CommonJS pur (seule dépendance : ./caution-amount.js, CommonJS lui aussi) :
// l'aperçu des messages (src/lib/template-variables.js) exécute ainsi le
// MÊME code de caution que l'envoi réel ; le serveur le lit par le relais `lib/journey-composition-resolver.js`. Ne rien
// ajouter qui ne soit chargeable des deux côtés (garde :
// src/lib/doublons-lib-conformite.test.js, « sources uniques »).
//
// Résolveur PUR (zéro I/O, zéro dépendance) de la composition de l'espace
// voyageur : quels blocs (formulaire d'arrivée, caution, livret, reçu,
// état des lieux)
// afficher pour un séjour donné, selon la plateforme source et la
// configuration `journey_composition_config` (migration 20261018000001).
//
// Cascade STRICTE, résolue BLOC PAR BLOC :
//   override LOGEMENT (scope = logement_id, exception rare)
//     > matrice GLOBALE par plateforme (scope = 'global')
//       > défauts codés (CODED_DEFAULTS)
// 'auto' (ou cellule absente / illisible) = déléguer au niveau inférieur.
//
// Fail-safe (doctrine : ne JAMAIS casser l'espace voyageur) :
//   - configRows absent / non-array / lignes illisibles → ignorées → défauts codés.
//   - source inconnue → plateforme null + TOUS les blocs affichés (sur-afficher
//     un bloc est bénin, en cacher un nécessaire ne l'est pas).
//
// ⚠️ COLLISION INTERDITE (Lot O1 parallèle) : le lot O1 livre
// `resolveOtaJourneyBlocks(source)` dans lib/ — ce module-ci est SÉPARÉ et ne
// le touche pas. Le branchement final (post-merge des deux lots) consommera ce
// résolveur pour la partie config ; suivi noté dans la PR du Lot O2.
//
// Consommateurs : lib/journey-composition-handlers.js (endpoints admin — la
// résolution « effective » renvoyée à l'UI vient d'ici) + branchement espace
// voyageur OTA (chantier en cours, post-merge O1+O2).

const PLATEFORMES = ["booking", "airbnb", "direct"];
// `avis` (6e bloc, système d'avis « option a », 2026-07-22) : pilote le prompt
// ressenti en phase départ. À LA DIFFÉRENCE des 5 autres, son défaut codé est
// 'off' PARTOUT (y compris fail-safe source inconnue) — c'est une feature
// DORMANTE, légalement sensible (gate avocat) : sur-afficher `avis` n'est PAS
// bénin (ce serait activer la sollicitation sélective). Doctrine INVERSE des
// autres blocs, volontaire et documentée.
const BLOCS = ["formulaire", "caution", "livret", "recu", "etat_lieux", "avis"];
const MODES = ["auto", "on", "off"];

// Défauts codés (décisions Dimitri figées Lot O2 ; etat_lieux ajouté 2026-07-21,
// cadrage État des lieux validé) — mode 'auto' intégral :
//   booking → formulaire + caution + livret + reçu + état des lieux
//   airbnb  → formulaire + livret + reçu + état des lieux (caution Airbnb → off)
//   direct  → tout (parcours direct complet)
// etat_lieux = 'on' pour les 3 plateformes (décision produit : proposé
// partout, jamais bloquant). Décision produit post-démo : le module état des
// lieux voyageur vivra DANS le livret d'accueil (pas comme bloc séparé de
// l'espace voyageur OTA) — cette cellule pilotera la section du livret.
// `avis` = false PARTOUT (dormance : feature légalement gatée, activée seulement
// par cellule matrice explicite + flag env AVIS_GATING_ENABLED).
const CODED_DEFAULTS = Object.freeze({
  booking: Object.freeze({
    formulaire: true,
    caution: true,
    livret: true,
    recu: true,
    etat_lieux: true,
    avis: false,
  }),
  airbnb: Object.freeze({
    formulaire: true,
    caution: false,
    livret: true,
    recu: true,
    etat_lieux: true,
    avis: false,
  }),
  direct: Object.freeze({
    formulaire: true,
    caution: true,
    livret: true,
    recu: true,
    etat_lieux: true,
    avis: false,
  }),
});

// Fail-safe source inconnue : TOUT afficher (jamais de bloc nécessaire caché)
// — SAUF `avis` (dormance : sur-afficher le prompt ressenti n'est PAS bénin,
// c'est activer la sollicitation sélective sans le vouloir → off au fail-safe).
const FAILSAFE_ALL_ON = Object.freeze({
  formulaire: true,
  caution: true,
  livret: true,
  recu: true,
  etat_lieux: true,
  avis: false,
});

/**
 * Normalise une source de réservation vers une plateforme de la matrice.
 * Conservateur : 'booking' / 'booking.com' → 'booking' ; 'airbnb' → 'airbnb' ;
 * 'direct' → 'direct'. Toute autre valeur → null (fail-safe géré par
 * resolveJourneyComposition).
 *
 * @param {string} source
 * @returns {'booking'|'airbnb'|'direct'|null}
 */
function normalizeSourceToPlateforme(source) {
  if (typeof source !== "string") return null;
  const s = source.trim().toLowerCase();
  if (s === "booking" || s === "booking.com") return "booking";
  if (s === "airbnb") return "airbnb";
  if (s === "direct") return "direct";
  return null;
}

// Mode d'une cellule d'une ligne config : 'on' | 'off' | 'auto'.
// Ligne absente, blocs non-objet, clé absente ou valeur inconnue → 'auto'
// (fail-safe : une cellule illisible délègue au niveau inférieur).
function _cellMode(row, bloc) {
  if (!row || typeof row !== "object") return "auto";
  const blocs = row.blocs;
  if (!blocs || typeof blocs !== "object" || Array.isArray(blocs))
    return "auto";
  const v = blocs[bloc];
  return MODES.includes(v) ? v : "auto";
}

// Retrouve la ligne (scope, plateforme) dans configRows. Lignes illisibles
// ignorées. Renvoie null si absente.
function _findRow(configRows, scope, plateforme) {
  if (!Array.isArray(configRows)) return null;
  return (
    configRows.find(
      (r) =>
        r &&
        typeof r === "object" &&
        r.scope === scope &&
        r.plateforme === plateforme,
    ) || null
  );
}

/**
 * Résout la composition effective de l'espace voyageur.
 *
 * @param {object} params
 * @param {string} params.source       — source de la résa ('booking'|'airbnb'|'direct'…)
 * @param {string} [params.logementId] — logement concerné (optionnel : sans lui,
 *                                       la cascade est global > défauts)
 * @param {Array<object>} [params.configRows] — lignes journey_composition_config
 *                                       (scope, plateforme, blocs). Peut contenir
 *                                       global + overrides mélangés.
 * @returns {{
 *   plateforme: 'booking'|'airbnb'|'direct'|null,
 *   blocs: {formulaire, caution, livret, recu, etat_lieux: boolean},
 *   provenance: {formulaire, caution, livret, recu, etat_lieux: string}
 * }} provenance par bloc ∈ 'logement' | 'global' | 'defaut' | 'failsafe'.
 */
function resolveJourneyComposition({ source, logementId, configRows } = {}) {
  const plateforme = normalizeSourceToPlateforme(source);

  if (!plateforme) {
    // Fail-safe source inconnue : tout afficher, provenance explicite.
    const provenance = {};
    for (const b of BLOCS) provenance[b] = "failsafe";
    return { plateforme: null, blocs: { ...FAILSAFE_ALL_ON }, provenance };
  }

  const logementRow =
    typeof logementId === "string" && logementId && logementId !== "global"
      ? _findRow(configRows, logementId, plateforme)
      : null;
  const globalRow = _findRow(configRows, "global", plateforme);
  const defaults = CODED_DEFAULTS[plateforme];

  const blocs = {};
  const provenance = {};
  for (const b of BLOCS) {
    const logementMode = _cellMode(logementRow, b);
    if (logementMode !== "auto") {
      blocs[b] = logementMode === "on";
      provenance[b] = "logement";
      continue;
    }
    const globalMode = _cellMode(globalRow, b);
    if (globalMode !== "auto") {
      blocs[b] = globalMode === "on";
      provenance[b] = "global";
      continue;
    }
    blocs[b] = defaults[b];
    provenance[b] = "defaut";
  }

  return { plateforme, blocs, provenance };
}

// ════════════════════════════════════════════════════════════════════
// LOT 2 — CAUTION PAR PLATEFORME : dérivation UNIQUE « caution due ? »
// ════════════════════════════════════════════════════════════════════
//
// Le désordre historique = plusieurs sources décidaient « caution due ? » sans
// s'accorder. LOT 2 pose UNE dérivation, DEUX intrants persistés : le MONTANT
// (lib/caution-amount.js::resolveCautionFromLog) ET la CELLULE matrice
// (resolveJourneyComposition). On ne stocke JAMAIS « caution due » : toute
// surface recalcule au moment de la génération (invariant read-time).
//
// Invariant SACRÉ : matrice VIDE / illisible → CODED_DEFAULTS (booking/direct
// caution = on, airbnb = off) → dérivation byte-identique à l'existant.

const { resolveCautionFromLog } = require("./caution-amount.js");

/**
 * Pré-normalisation caution : mappe les variants iCal V1
 * `booking_com`/`bookingdotcom` → `booking` AVANT le resolver.
 *
 * SANS elle, `normalizeSourceToPlateforme` envoie `booking_com` → null →
 * FAILSAFE_ALL_ON (caution=true) QUELLE QUE SOIT la matrice — une cellule
 * `booking.caution:'off'` serait alors MUETTE pour tout le parc iCal (§2.4 du
 * design, TROU money/juridique confirmé). Aligné sur
 * lib/payment-derivation.js::normalizeSource (casse-insensible, mêmes variants).
 *
 * @param {*} source
 * @returns {string} source minusculée/trimée, variants booking pré-mappés
 */
function _normalizeCautionSource(source) {
  const s = (source || "").toString().trim().toLowerCase();
  if (s === "booking_com" || s === "bookingdotcom") return "booking";
  return s;
}

/**
 * La CELLULE matrice `caution` est-elle ON pour ce (source, logement) ?
 * Terme « matrice » de la dérivation, ISOLÉ (sans le terme montant) — utilisé
 * par les surfaces dont le comportement actuel N'EST PAS gardé par un montant
 * (ex. mint inconditionnel M-5 du checkout Direct : y ajouter le terme montant
 * changerait le comportement matrice-vide, ce que l'invariant SACRÉ interdit).
 *
 *   airbnb              → false (PLANCHER DUR AirCover, jamais de caution).
 *   matrice illisible   → CODED_DEFAULTS (booking/direct = on) → byte-identique.
 *
 * @param {object} params
 * @param {string} params.source
 * @param {string} [params.logementId]
 * @param {Array<object>|null} [params.configRows]
 * @returns {boolean}
 */
function matrixCautionOn({ source, logementId, configRows } = {}) {
  const plateforme = normalizeSourceToPlateforme(_normalizeCautionSource(source));
  if (plateforme === "airbnb") return false; // plancher dur AirCover
  return (
    resolveJourneyComposition({ source: plateforme, logementId, configRows })
      .blocs.caution === true
  );
}

/**
 * La CELLULE matrice `formulaire` est-elle ON pour ce (source, logement) ?
 * Jumeau EXACT de `matrixCautionOn` pour le bloc formulaire — même
 * pré-normalisation des variants Booking, même cascade logement > global >
 * CODED_DEFAULTS, même fail-open (matrice illisible → défauts codés, où le
 * formulaire est ON pour les trois plateformes).
 *
 * Consommé par la garde « pas de caution sans formulaire » (lib/caution-prereq.js) :
 * la règle ne s'applique QUE là où le formulaire est réellement demandé au
 * voyageur. Un logement dont la matrice éteint le formulaire ne peut donc PAS
 * se retrouver avec une caution bloquée par un formulaire qui n'existe pas —
 * pas de cul-de-sac par construction.
 *
 * Pas de plancher dur ici (contrairement à airbnb pour la caution) : le
 * formulaire est demandé sur les trois plateformes par défaut.
 *
 * @param {object} params
 * @param {string} params.source
 * @param {string} [params.logementId]
 * @param {Array<object>|null} [params.configRows]
 * @returns {boolean}
 */
function matrixFormulaireOn({ source, logementId, configRows } = {}) {
  const plateforme = normalizeSourceToPlateforme(_normalizeCautionSource(source));
  return (
    resolveJourneyComposition({ source: plateforme, logementId, configRows })
      .blocs.formulaire === true
  );
}

/**
 * UNIQUE dérivation « caution due pour ce séjour ? » = (montant > 0) ET
 * (cellule matrice caution ON). `amount` est le montant DÉJÀ résolu par le
 * call-site quand il l'a (E1/E3/contrat/livret/Stripe) ; sinon résolu depuis
 * `log` via resolveCautionFromLog.
 *
 * Trois directions de fail, toutes SÛRES :
 *   - runtime matrice-KO → configRows null → CODED_DEFAULTS → caution ON
 *     (booking/direct) : jamais un paiement légitime cassé par un incident DB ;
 *   - airbnb → toujours false (plancher) ;
 *   - montant absent/≤0 → false (pas de montant → pas de caution).
 *
 * Matrice VIDE → `(montant > 0)` pur = gate montant actuel = BYTE-IDENTIQUE.
 *
 * @param {object} params
 * @param {string} params.source            — VRAIE source de la résa/bloc (polymorphe)
 * @param {string} [params.logementId]
 * @param {Array<object>|null} [params.configRows]
 * @param {object|null} [params.log]        — log cfg (fallback montant si amount absent)
 * @param {number} [params.amount]          — montant déjà résolu (prioritaire)
 * @returns {boolean}
 */
function isCautionDueForStay({ source, logementId, configRows, log, amount } = {}) {
  const plateforme = normalizeSourceToPlateforme(_normalizeCautionSource(source));
  if (plateforme === "airbnb") return false; // plancher dur AirCover
  const montant = amount !== undefined ? amount : resolveCautionFromLog(log);
  if (montant == null || !(Number(montant) > 0)) return false;
  return (
    resolveJourneyComposition({ source: plateforme, logementId, configRows })
      .blocs.caution === true
  );
}

/**
 * Normalise un objet blocs vers la forme canonique complète
 * {formulaire, caution, livret, recu, etat_lieux} avec valeurs ∈ MODES ('auto',
 * valeurs illisibles → 'auto'). PUR — utilisé par les endpoints (lecture) et
 * l'UI (affichage).
 */
function normalizeBlocs(blocs) {
  const out = {};
  const src =
    blocs && typeof blocs === "object" && !Array.isArray(blocs) ? blocs : {};
  for (const b of BLOCS) out[b] = MODES.includes(src[b]) ? src[b] : "auto";
  return out;
}

/**
 * Validation STRICTE d'un objet blocs entrant (endpoint set) :
 * objet non-array, clés ⊆ BLOCS, valeurs ∈ MODES. Clé absente = 'auto'.
 * @returns {{ok: true, blocs: object} | {ok: false, error: string}}
 */
function validateBlocsInput(blocs) {
  if (!blocs || typeof blocs !== "object" || Array.isArray(blocs)) {
    return { ok: false, error: "blocs_invalide" };
  }
  for (const key of Object.keys(blocs)) {
    if (!BLOCS.includes(key))
      return { ok: false, error: `bloc_inconnu:${key}` };
    if (!MODES.includes(blocs[key])) {
      return { ok: false, error: `valeur_invalide:${key}` };
    }
  }
  return { ok: true, blocs: normalizeBlocs(blocs) };
}

/**
 * Une config (ligne ou ensemble de lignes d'un même scope) est une exception
 * ACTIVE si au moins une cellule ≠ 'auto'. Un override tout-'auto' équivaut à
 * « suivre le global » (c'est la forme « Revenir au global » — jamais de DELETE).
 */
function hasActiveOverride(rows) {
  const list = Array.isArray(rows) ? rows : [rows];
  return list.some((row) => {
    if (!row || typeof row !== "object") return false;
    const norm = normalizeBlocs(row.blocs);
    return BLOCS.some((b) => norm[b] !== "auto");
  });
}

module.exports = {
  PLATEFORMES,
  BLOCS,
  MODES,
  CODED_DEFAULTS,
  FAILSAFE_ALL_ON,
  normalizeSourceToPlateforme,
  resolveJourneyComposition,
  normalizeBlocs,
  validateBlocsInput,
  hasActiveOverride,
  // LOT 2 — caution par plateforme (dérivation unique).
  _normalizeCautionSource,
  matrixCautionOn,
  matrixFormulaireOn,
  isCautionDueForStay,
  _internal: { _cellMode, _findRow },
};
